import { IPublicClientApplication } from '@azure/msal-browser';

// Minimal download payload the endpoint expects.
interface AnalysisDownloadMinimalPayload {
  container_id: string;
  file_id: string;
}

/**
 * Log-only mode: trigger API call and console log metadata; no popup fallback, no file save.
 * Extended to optionally accept a filename for clearer diagnostics.
 * Preview mode: fetch file data and return it without downloading.
 */
export async function downloadAnalysisFile(
  msalInstance: IPublicClientApplication,
  containerId: string,
  fileId: string,
  filename?: string,
  options?: { 
    logOnly?: boolean; 
    autoDownloadCsvIfText?: boolean; 
    captureRaw?: (raw: string | ArrayBuffer, mode: 'text' | 'binary' | 'empty') => void;
    previewMode?: boolean; // If true, return file data instead of downloading
  }
): Promise<void | { data: string | ArrayBuffer; type: 'text' | 'binary'; contentType?: string }> {
  if (!containerId || !fileId) {
    // eslint-disable-next-line no-console
    console.warn('[file-download] missing ids', { containerId, fileId, filename });
    return;
  }
  // Prefer APIM unless explicitly overridden to local.
  const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';
  const envOverride = import.meta.env.VITE_FILE_DOWNLOAD_URL || undefined;
  const useLocal = import.meta.env.VITE_USE_LOCAL_FILE_DOWNLOAD === 'true';
  const baseUrl = envOverride || (useLocal
    ? 'http://localhost:8006/ai/containers/files/download'
    : `${apimBaseUrl}/nav-api/api/ai/file-download`);

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('[file-download] no account');
    return;
  }
  const request = { scopes: [`${import.meta.env.VITE_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`], account: accounts[0] };
  let accessToken: string | undefined;
  try {
    accessToken = (await msalInstance.acquireTokenSilent(request)).accessToken;
  } catch (err) {
    // Silent only; do not popup/redirect.
    // eslint-disable-next-line no-console
    console.warn('[file-download] silent token failed', err);
    return;
  }
  if (!accessToken) {
    // eslint-disable-next-line no-console
    console.warn('[file-download] no access token');
    return;
  }
  const payload: AnalysisDownloadMinimalPayload = {
    container_id: containerId,
    file_id: fileId
  };
  const payloadJson = JSON.stringify(payload);
  let resp: Response;
  try {
    resp = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Accept': '*/*'
      },
      body: payloadJson
    });
    // Success/response preview logs removed per user request; retain only error/warn below.
    // Fallback retry for known APIM path mismatch (403) using non-/ai base path
    if (resp.status === 403 && /\/api\/ai\/file-download/.test(baseUrl)) {
      const altUrl = baseUrl.replace('/api/ai/file-download', '/api/file-download');
      try {
        const altResp = await fetch(altUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': '*/*'
          },
          body: payloadJson
        });
        if (altResp.ok || altResp.status !== 403) {
          console.warn('[file-download] primary 403; alt path attempted');
          resp = altResp;
        }
      } catch (e) {
        console.warn('[file-download] alt path fetch error', e);
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[file-download] fetch error', err);
    return;
  }
  // Attempt to extract identifiers returned by API (if JSON response).
  const ct = resp.headers.get('Content-Type');
  const dispo = resp.headers.get('Content-Disposition');

  // Read entire raw body exactly as received.
  let rawBody: string | ArrayBuffer | null = null;
  let bodyMode: 'text' | 'binary' | 'empty' = 'empty';
  try {
    if (resp.ok) {
      // Force binary mode for known binary file types (PDFs, images, etc.)
      const isBinaryContentType = ct && (
        /application\/pdf/i.test(ct) ||
        /image\//i.test(ct) ||
        /application\/zip/i.test(ct) ||
        /application\/x-zip/i.test(ct) ||
        /application\/vnd\./i.test(ct)
      );
      
      if (isBinaryContentType) {
        // Force binary for known binary types
        const arr = await resp.arrayBuffer();
        rawBody = arr; bodyMode = 'binary';
      } else {
        // Try text first (many CSVs come back as octet-stream but are still text)
        const cloned = resp.clone();
        const textAttempt = await cloned.text();
        // Heuristic: consider it text if high ratio of printable chars.
        const printableRatio = textAttempt.length ? (textAttempt.match(/[\x20-\x7E\t\r\n]/g)?.length || 0) / textAttempt.length : 0;
        if (printableRatio > 0.85) {
          rawBody = textAttempt; bodyMode = 'text';
        } else {
          // Fallback to binary
          const arr = await resp.arrayBuffer();
          rawBody = arr; bodyMode = 'binary';
        }
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[file-download] body-read-error', (e as Error).message);
  }

  // Invoke optional capture callback with full raw body prior to any transformation.
  try { options?.captureRaw && options.captureRaw(rawBody ?? new ArrayBuffer(0), bodyMode); } catch {/* ignore user callback errors */}

  // If preview mode is enabled, return the file data instead of downloading
  if (options?.previewMode && rawBody && bodyMode !== 'empty') {
    return {
      data: rawBody,
      type: bodyMode,
      contentType: ct || undefined
    };
  }

  // Heuristic CSV detection (independent of headers): first line has >2 commas and no binary control chars.
  let detectedCsv = false;
  let csvFirstLine: string | undefined;
  if (bodyMode === 'text' && typeof rawBody === 'string') {
    csvFirstLine = rawBody.split(/\r?\n/)[0] || '';
    const commaCount = (csvFirstLine.match(/,/g) || []).length;
    const hasControl = [...csvFirstLine].some(ch => { const code = ch.charCodeAt(0); return code < 32 && code !== 9 && code !== 10 && code !== 13; });
    detectedCsv = commaCount >= 3 && !hasControl;
  }

  // Optional real download if not logOnly.
  if (resp.ok && rawBody) {
    // Auto-download path for CSV text if enabled and not in log-only mode.
    const shouldAutoDownloadCsv = detectedCsv && (options?.autoDownloadCsvIfText ?? false) && !(options?.logOnly ?? true);
    const shouldDirectDownload = !(options?.logOnly ?? true) && !shouldAutoDownloadCsv; // preserve previous behavior if logOnly false but auto not requested.
    if (shouldAutoDownloadCsv) {
      try {
        const blob = new Blob([rawBody as string], { type: 'text/csv' });
        // Derive filename: prefer provided, else content-disposition (replace .bin with .csv), else generic.
        let dlName = filename || /filename="([^"]+)"/i.exec(dispo || '')?.[1] || 'download.csv';
        if (/\.bin$/i.test(dlName)) dlName = dlName.replace(/\.bin$/i, '.csv');
        if (!/\.csv$/i.test(dlName)) dlName += '.csv';
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = dlName; document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 2000);
      } catch (e) {
        console.warn('[file-download] auto-csv-download-error', (e as Error).message);
      }
    } else if (shouldDirectDownload) {
      // previous behavior
      try {
        if (bodyMode === 'text') {
          const blob = new Blob([rawBody as string], { type: /csv/i.test(ct || '') ? 'text/csv' : 'text/plain' });
          const dlName = filename || /filename="([^"]+)"/i.exec(dispo || '')?.[1] || (detectedCsv ? 'download.csv' : 'download.txt');
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = dlName; document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 2000);
        } else if (bodyMode === 'binary') {
          const blob = new Blob([rawBody as ArrayBuffer], { type: ct || 'application/octet-stream' });
          const dlName = filename || /filename="([^"]+)"/i.exec(dispo || '')?.[1] || 'download.bin';
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = dlName; document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 2000);
        }
      } catch (e) {
        console.warn('[file-download] download-error', (e as Error).message);
      }
    }
  }
  // Intentionally do not read or process body in log-only mode.
  if (!resp.ok) {
    // eslint-disable-next-line no-console
    console.warn('[file-download] non-OK status', resp.status, { endpoint: baseUrl, payload });
  }
}
